import React, { useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { AppProvider, Router, Session } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import { useOptionalAuth } from 'contexts/AuthContext';
import { NoticeCountProvider } from 'contexts/NoticeCountContext';

import AppTitle from 'components/layout/AppTitle';
import ToolbarAccount from 'components/layout/ToolbarAccount';
import {
  NoticeToolbarBadge,
  dashboardTheme,
  handleAddAccount,
  handleSwitchAccount,
  signInOut,
  toSession,
} from 'layouts/router/common';

const DevDocsLayout: React.FC = () => {
  const { userSession } = useOptionalAuth();
  const isGuest = userSession === null;
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  const router = useRouter('/devdocs');

  const authentication = React.useMemo(() => signInOut, []);

  // ページ遷移時にスクロールをトップに戻す
  React.useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const session: Session | null = React.useMemo(
    () => (userSession ? toSession(userSession) : null),
    [userSession],
  );

  const ToolbarAccountSlot = React.useCallback(() => {
    // ゲスト時はアカウントメニューの代わりに「ログイン」導線を表示する
    if (isGuest) {
      return (
        <Button
          size="small"
          variant="outlined"
          startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/login')}
          sx={{
            ml: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          ログイン
        </Button>
      );
    }
    return (
      <ToolbarAccount
        userSession={userSession}
        onSignOut={signInOut.signOut}
        onSwitchAccount={handleSwitchAccount}
        onAddAccount={handleAddAccount}
      />
    );
  }, [isGuest, userSession, navigate]);

  const ToolbarActionsSlot = React.useCallback(() => {
    // ゲスト時は通知バッジ等を非表示にする
    if (isGuest) {
      return <GuestChip />;
    }
    return (
      <NoticeToolbarBadge
        allowedScreens={userSession.allowedScreens}
        userName={userSession.userName}
      />
    );
  }, [isGuest, userSession]);

  const content = (
    <AppProvider
      session={session}
      authentication={authentication}
      navigation={[]}
      router={router}
      theme={dashboardTheme}
    >
      <DashboardLayout
        hideNavigation
        slots={{
          appTitle: DevInsightsAppTitle,
          toolbarActions: ToolbarActionsSlot,
          toolbarAccount: ToolbarAccountSlot,
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            pt: 1.5,
            pl: 2,
            pr: 2,
            pb: 1.5,
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          {/*
            ホームに戻るボタン。
            - ログイン時のみ（ゲストはメインメニューへ戻れないため非表示）。
            - 記事ページ（/devdocs/article 配下）ではページ側が「一覧に戻る」を出すため非表示。
            - DevInsights トップ（/devdocs）は共通ヒーロー内に「ホームに戻る」を表示するため非表示（他ドメインと統一）。
          */}
          {!isGuest &&
            !location.pathname.startsWith('/devdocs/article') &&
            location.pathname !== '/devdocs' &&
            location.pathname !== '/devdocs/' && (
              <Box sx={{ mb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                  }}
                >
                  ホームに戻る
                </Button>
              </Box>
            )}
          <Outlet />
        </Box>
      </DashboardLayout>
    </AppProvider>
  );

  // ゲスト時は通知APIを呼ぶ NoticeCountProvider を挟まない
  return isGuest ? content : <NoticeCountProvider>{content}</NoticeCountProvider>;
};

/** ゲスト状態を示すチップ（ツールバーアクション枠に表示） */
function GuestChip() {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.25,
        mr: 1,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem' }}>G</Avatar>
      <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
        Guest
      </Box>
    </Box>
  );
}

function useRouter(initialPath: string): Router {
  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => {
        navigate(String(path));
        setPathname(String(path));
      },
    };
  }, [pathname, navigate]);

  return router;
}

function DevInsightsAppTitle() {
  return <AppTitle icon={<AutoStoriesIcon />} title="Dev Insights" color="#455a64" />;
}

export default DevDocsLayout;
